import React, { useMemo, useState } from "react";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useNavigate } from "react-router-dom";
import { School } from "models/school";
import FollowerRegistration from 'components/FollowerRegistration/FollowerRegistration';
import { SchoolEvent } from "models/event";
import { Tournament } from "models/tournament";
import { getDisplayMatchResult } from "helpers/matchResult";
import { useTeamTournamentLiveData } from "hooks/useTeamTournamentLiveData";
import "./TeamTournamentLive.scss";
import "./TeamTournamentKnockoutNote.scss";
import SponsorQR from "components/SponsorQR";
import { canRevealRuntimeKnockoutTeams } from "helpers/knockoutVisibility";
import "./TeamTournamentSponsorQR.scss";

interface Props {
  school: School;
  tournament: Tournament;
  embedded?: boolean;
  eventPathPrefix?: string;
}
type Tab = "NOW" | "GROUPS" | "QUALIFIERS" | "KNOCKOUT" | "FIXTURES" | "SCHOOLS";
const tabs: Array<{ id: Tab; label: string }> = [
  { id: "NOW", label: "Now" },
  { id: "GROUPS", label: "Groups" },
  { id: "QUALIFIERS", label: "Qualifiers" },
  { id: "KNOCKOUT", label: "Knockout Stages" },
  { id: "FIXTURES", label: "Fixtures & results" },
  { id: "SCHOOLS", label: "Schools" }
];
const sid = (value: any) => String(value?.id || value?._id || value || "");
const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(x => x[0])
    .join("")
    .toUpperCase();
const time = (value: string) =>
  new Date(value).toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit"
  });
const date = (value: string) =>
  new Date(value).toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric"
  });
const shortDate = (value: string) =>
  new Date(value).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric"
  });
const ordinal = (n?: number) =>
  `${n || ""}${n === 1 ? "st" : n === 2 ? "nd" : n === 3 ? "rd" : "th"}`;
const tieBreakerLabels: Record<string, string> = {
  GOAL_DIFFERENCE: "goal difference",
  GOALS_FOR: "goals scored",
  GOALS_AGAINST: "fewest goals conceded",
  WINS: "matches won",
  HEAD_TO_HEAD_POINTS: "the head-to-head result",
  HEAD_TO_HEAD_GOAL_DIFFERENCE: "head-to-head goal difference",
  HEAD_TO_HEAD_GOALS_FOR: "head-to-head goals scored",
  FAIR_PLAY: "fair play record",
  MANUAL_DRAW: "a decision or drawing of lots by the organiser"
};
const knockoutRounds = [
  { id: "ROUND_OF_64", label: "1/64 final", shortLabel: "1/64" },
  { id: "ROUND_OF_32", label: "1/32 final", shortLabel: "1/32" },
  { id: "ROUND_OF_16", label: "1/16 final", shortLabel: "1/16" },
  { id: "ROUND_OF_8", label: "1/8 final", shortLabel: "1/8" },
  { id: "QUARTER_FINAL", label: "Quarter-finals", shortLabel: "QF" },
  { id: "SEMI_FINAL", label: "Semi-finals", shortLabel: "SF" },
  { id: "THIRD_PLACE", label: "3rd/4th play-off", shortLabel: "3/4 Play-off" },
  { id: "FINAL", label: "Final", shortLabel: "Final" },
  { id: "PLAYOFF", label: "Play-offs & placing", shortLabel: "Play-off" }
];

const schoolForTeam = (event: SchoolEvent, team: any) =>
  ([event.inviterSchool, ...(event.invitedSchools || [])] as any[]).find(
    item => sid(item) === sid(team?.schoolId)
  );
const eventTeam = (event: SchoolEvent, index: number) => {
  const team: any =
    (event.teamsData || [])[index] || (event.embeddedTeams || [])[index];
  const school: any = schoolForTeam(event, team);
  return {
    id: sid(team?.cloneOf || team),
    name: team?.name || `Team ${index + 1}`,
    schoolId: sid(team?.schoolId),
    schoolName: school?.name || team?.schoolName || "",
    logo: school?.pic || team?.schoolPic || ""
  };
};
const TeamLogo = ({ team }: { team: any }) => (
  <span className="ttlCrest">
    {team.logo ? (
      <img src={team.logo} alt="" />
    ) : (
      initials(team.schoolName || team.name || "T")
    )}
  </span>
);
const TeamLine = ({ team, origin }: { team: any; origin?: string }) => (
  <span className="ttlTeamLine">
    <TeamLogo team={team} />
    <span>
      <b>{team.schoolName || team.name}</b>
      {team.schoolName && team.name && team.name !== team.schoolName && <small>{team.name}</small>}
      {origin && <small>{origin}</small>}
    </span>
  </span>
);

const fixtureStart = (value?: string) => {
  const timestamp = Date.parse(value || '');
  return Number.isFinite(timestamp) ? timestamp : Infinity;
};
const byFixtureStart = (a: {startTime?: string; id?: string; tournamentStructureMatchId?: string}, b: {startTime?: string; id?: string; tournamentStructureMatchId?: string}) =>
  (fixtureStart(a.startTime) - fixtureStart(b.startTime)) ||
  String(a.tournamentStructureMatchId || a.id || '').localeCompare(String(b.tournamentStructureMatchId || b.id || ''));

const scoreFor = (event: SchoolEvent) => getDisplayMatchResult(event);
const completed = (event: SchoolEvent) =>
  Boolean(scoreFor(event)) || event.status === "FINISHED";
const sponsorHref = (value?: string) => {
  if (!value) return "";
  try {
    const url = new URL(
      /^https?:\/\//i.test(value) ? value : `https://${value}`
    );
    return ["http:", "https:"].includes(url.protocol) ? url.href : "";
  } catch {
    return "";
  }
};

export default function TeamTournamentLive({
  school,
  tournament,
  embedded = false,
  eventPathPrefix = ""
}: Props) {
  const navigate = useNavigate();
  const {
    events,
    teams,
    runtime,
    isLoading,
    error,
    refresh
  } = useTeamTournamentLiveData(school.id, tournament.id);
  const followKey = `team-tournament:${tournament.id}:school`;
  const isMultiDay =
    new Date(tournament.startTime).toDateString() !==
    new Date(tournament.endTime).toDateString();
  const tournamentDates = isMultiDay
    ? `${shortDate(tournament.startTime)} – ${shortDate(tournament.endTime)}`
    : date(tournament.startTime);
  const [tab, setTab] = useState<Tab>("NOW");
  const [query, setQuery] = useState("");
  const [groupId, setGroupId] = useState("");
  const [knockoutGroupId, setKnockoutGroupId] = useState("");
  const [mobileKnockoutRoundId, setMobileKnockoutRoundId] = useState("");
  const isMobileKnockout = useMediaQuery("(max-width:760px)");
  const [hiddenKnockoutRounds, setHiddenKnockoutRounds] = useState<string[]>(
    []
  );
  const [schoolId, setSchoolId] = useState(() => {
    try {
      return window.localStorage.getItem(followKey) || "";
    } catch {
      return "";
    }
  });
  const [showMatrix, setShowMatrix] = useState(false);
  const [onlyMySchool, setOnlyMySchool] = useState(false);
  const [standingsStageId, setStandingsStageId] = useState('');
  const [standingsGroupId, setStandingsGroupId] = useState('');
  const stages = tournament.tournamentLayout || [];
  const structure =
    runtime?.structure || tournament.competitionStructure?.published;
  const configuredStageOrder = structure?.stageOrder || [];
  const configuredGroupOrder = structure?.groupOrder || [];
  const stageRank = (id: string) => {
    const configured = configuredStageOrder.indexOf(id);
    return configured >= 0
      ? configured
      : configuredStageOrder.length +
          (stages.find(stage => stage.id === id)?.order ??
            stages.findIndex(stage => stage.id === id));
  };
  const groupRank = (id: string) => {
    const configured = configuredGroupOrder.indexOf(id);
    return configured >= 0 ? configured : Number.MAX_SAFE_INTEGER;
  };
  const groups = [...(tournament.customGroups || [])].sort(
    (a, b) =>
      stageRank(a.stageId) - stageRank(b.stageId) ||
      groupRank(a.id) - groupRank(b.id) ||
      a.name.localeCompare(b.name, undefined, {
        numeric: true,
        sensitivity: "base"
      })
  );
  const stageType = (group: any) =>
    stages.find(stage => stage.id === group.stageId)?.type;
  const qualifierIds = new Set((structure?.qualifierGroups || []).map((pool: any) => String(pool.groupId)));
  const isRankingGroup = (group: any) => stageType(group) === "GROUP" || qualifierIds.has(group.id);
  const groupGroups = groups.filter(isRankingGroup);
  const standingsStages = stages.filter(stage => groupGroups.some(group => group.stageId === stage.id))
    .sort((a, b) => stageRank(a.id) - stageRank(b.id));
  const selectedStandingsStage = standingsStages.some(stage => stage.id === standingsStageId) ? standingsStageId : '';
  const standingsGroupOptions = groupGroups.filter(group => !selectedStandingsStage || group.stageId === selectedStandingsStage);
  const selectedStandingsGroup = standingsGroupOptions.some(group => group.id === standingsGroupId) ? standingsGroupId : '';
  const dashboardStandings = standingsGroupOptions.filter(group => !selectedStandingsGroup || group.id === selectedStandingsGroup);
  const knockoutGroups = groups.filter(g => !isRankingGroup(g));
  const structureGroups = groups.filter(g => stageType(g) !== "GROUP");
  const sorted = useMemo(() => [...events].sort(byFixtureStart), [events]);
  const now = Date.now();
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sorted;
    return sorted.filter(event =>
      [eventTeam(event, 0), eventTeam(event, 1)].some(team =>
        `${team.name} ${team.schoolName}`.toLowerCase().includes(q)
      )
    );
  }, [sorted, query]);
  const liveAll = sorted.filter(
    e =>
      !completed(e) &&
      +new Date(e.startTime) <= now &&
      +new Date(e.endTime || e.startTime) + 90 * 60000 >= now
  );
  const upcomingAll = sorted.filter(
    e => !completed(e) && +new Date(e.startTime) > now
  );
  const live = filtered.filter(
    e =>
      !completed(e) &&
      +new Date(e.startTime) <= now &&
      +new Date(e.endTime || e.startTime) + 90 * 60000 >= now
  );
  const upcoming = filtered.filter(
    e => !completed(e) && +new Date(e.startTime) > now
  );
  const finished = filtered.filter(completed);
  const schoolList = (tournament.schools || []).filter(
    item => !query || item.name.toLowerCase().includes(query.toLowerCase())
  );
  const followedSchool = (tournament.schools || []).find(
    item => item.id === schoolId
  );
  const chooseSchool = (id: string) => {
    setSchoolId(id);
    try {
      id
        ? window.localStorage.setItem(followKey, id)
        : window.localStorage.removeItem(followKey);
    } catch {
      /* Storage is optional. */
    }
  };
  const includesSchool = (event: SchoolEvent, id: string) =>
    [eventTeam(event, 0), eventTeam(event, 1)].some(
      team => team.schoolId === id
    );
  const tournamentTeamIdentity = (teamId?: string) => {
    const team =
      (teams || []).find(item => sid(item) === sid(teamId)) ||
      (runtime?.teams || []).find((item: any) => sid(item) === sid(teamId));
    const participant = (tournament.schools || []).find(
      item => sid(item) === sid(team?.schoolId)
    );
    return team
      ? {
          id: sid(team),
          name: team.name || "Tournament team",
          schoolId: sid(team.schoolId),
          schoolName: participant?.name || team.schoolName || "",
          logo: participant?.pic || team.schoolPic || ""
        }
      : undefined;
  };
  const eventGroup = (event: SchoolEvent) =>
    groups.find(g => g.id === event.tournamentGroupId);
  const legacyRound = (group: any) =>
    qualifierIds.has(group.id) ? "GROUP" : (({
      QUALIFIER: "QUARTER_FINAL",
      PLAYOFF: "SEMI_FINAL",
      FINAL: "FINAL"
    } as Record<string, string>)[stageType(group) || ""] || "PLAYOFF");
  const matchRound = (match: any, group: any) =>
    match?.round || legacyRound(group);
  const structureMatchForEvent = (event: SchoolEvent) =>
    structure?.matches?.find(match => {
      const live = runtime?.matches?.find(
        (item: any) => sid(item.matchId) === sid(match.id)
      );
      return sid(live?.eventId || match.eventId) === sid(event) ||
        Boolean(event.tournamentStructureMatchId && sid(match.id) === sid(event.tournamentStructureMatchId));
    });
  const knockoutStageLabel = (event: SchoolEvent, group: any) => {
    if (!group || isRankingGroup(group)) return "";
    const round = matchRound(structureMatchForEvent(event), group);
    return (
      knockoutRounds.find(item => item.id === round)?.shortLabel ||
      String(round || "")
        .replace(/_/g, " ")
        .toLowerCase()
        .replace(/\b\w/g, letter => letter.toUpperCase())
    );
  };
  const pitch = (event: SchoolEvent) =>
    tournament.pitches?.find(p => p.id === event.pitchId)?.name || "";
  const sourceLabel = (source: any) =>
    source?.label ||
    (source?.sourceType === "BYE"
      ? "Bye"
      : source?.sourceType === "GROUP_POSITION"
      ? `${ordinal(source.sourcePosition)} · ${groups.find(
          g => g.id === source.sourceGroupId
        )?.name || "Group"}`
      : source?.sourceType === "TEAM"
      ? tournamentTeamIdentity(source.teamId)?.schoolName ||
        tournamentTeamIdentity(source.teamId)?.name ||
        "Tournament team"
      : `${
          source?.sourceType === "MATCH_LOSER" ? "Loser" : "Winner"
        } · ${structure?.matches?.find(m => m.id === source?.sourceMatchId)
          ?.name || "previous match"}`);
  const openEvent = (event: SchoolEvent) =>
    navigate(`${eventPathPrefix}/event/${event.id}`);
  const MatchCard = ({
    event,
    compact = false
  }: {
    event: SchoolEvent;
    compact?: boolean;
  }) => {
    const result = scoreFor(event),
      a = eventTeam(event, 0),
      b = eventTeam(event, 1),
      group = eventGroup(event);
    const fixture = structureMatchForEvent(event);
    const stageLabel = knockoutStageLabel(event, group);
    const embedded = event.embeddedTeams || [];
    const scorerLine = (index: number) =>
      (event.matchResult?.scorers || [])
        .filter(scorer => sid(scorer.teamId) === sid(embedded[index]))
        .map(
          scorer =>
            `${scorer.playerNameSnapshot}${scorer.ownGoal ? " (OG)" : ""}${
              scorer.minute != null
                ? ` ${scorer.minute}${
                    scorer.second != null
                      ? `:${String(scorer.second).padStart(2, "0")}`
                      : ""
                  }′`
                : ""
            }`
        )
        .join(", ");
    const scorerSummary = !compact && [scorerLine(0), scorerLine(1)];
    const resultLabel =
      result?.resolution === "SHOOTOUT"
        ? "Pens"
        : result?.resolution === "EXTRA_TIME"
        ? "AET"
        : result
        ? "Full time"
        : "";
    const resultStatusClass =
      result?.resolution === "SHOOTOUT"
        ? "is-pens"
        : result?.resolution === "EXTRA_TIME"
        ? "is-aet"
        : "is-full-time";
    return (
      <button
        className={`ttlMatchCard ${compact ? "is-compact" : ""}`}
        onClick={() => openEvent(event)}
      >
        <span className="ttlMatchCard__meta">
          <b>{Number.isFinite(fixtureStart(event.startTime)) ? time(event.startTime) : 'Time TBC'}</b>
          {pitch(event) && <span>{pitch(event)}</span>}
          <span>{fixture?.name || group?.name || "Tournament match"}</span>
          {!fixture?.name && stageLabel && (
            <span className="ttlMatchCard__round">{stageLabel}</span>
          )}
          {Number(event.tournamentRound) > 0 &&
            Number(group?.roundRobinRounds) > 1 && (
              <span className="ttlMatchCard__round">
                Round {event.tournamentRound}
              </span>
            )}
          {!result && live.includes(event) && (
            <em>
              <i />
              Live
            </em>
          )}
          {result && (
            <em className={`is-complete ${resultStatusClass}`}>
              {resultLabel}
            </em>
          )}
        </span>
        <span className="ttlMatchCard__matchup">
          <TeamLine team={a} origin={fixture?.homeOriginLabel} />
          <strong className={result ? "has-result" : ""}>
            {result ? (
              <>
                <b>{result.home}</b>
                <i>–</i>
                <b>{result.away}</b>
              </>
            ) : (
              <i>vs</i>
            )}
          </strong>
          <TeamLine team={b} origin={fixture?.awayOriginLabel} />
        </span>
        {(scorerSummary?.[0] || scorerSummary?.[1]) && (
          <span className="ttlMatchCard__scorerRows">
            {scorerSummary?.[0] && (
              <small className="ttlMatchCard__scorers">
                {scorerSummary[0]}
              </small>
            )}
            {scorerSummary?.[1] && (
              <small className="ttlMatchCard__scorers">
                {scorerSummary[1]}
              </small>
            )}
          </span>
        )}
        {result?.suffix && (
          <small className="ttlMatchCard__note">{result.suffix}</small>
        )}
      </button>
    );
  };
  const groupEvents = (id: string) =>
    filtered.filter(e => e.tournamentGroupId === id);
  const standings = (id: string) => {
    const resolved = runtime?.groups?.[id]?.standings;
    if (resolved) {
      return resolved.map((row: any) => {
        const team =
          teams.find(item => sid(item) === sid(row.teamId)) ||
          runtime.teams?.find((item: any) => sid(item) === sid(row.teamId)) ||
          {};
        const participant = tournament.schools?.find(
          item => sid(item) === sid(team.schoolId)
        );
        const display = {
          id: sid(row.teamId),
          name: team.name || "Tournament team",
          schoolId: sid(team.schoolId),
          schoolName: participant?.name || team.schoolName || "",
          logo: participant?.pic || team.schoolPic || ""
        };
        return {
          team: display,
          ...display,
          played: row.played,
          won: row.won,
          drawn: row.drawn,
          lost: row.lost,
          gf: row.goalsFor,
          ga: row.goalsAgainst,
          gd: row.goalDifference,
          points: row.points,
          isResolved: row.isResolved
        };
      });
    }
    const map = new Map<string, any>();
    const ensure = (team: any) => {
      if (!map.has(team.id))
        map.set(team.id, {
          team,
          ...team,
          played: 0,
          won: 0,
          drawn: 0,
          lost: 0,
          gf: 0,
          ga: 0,
          gd: 0,
          points: 0
        });
      return map.get(team.id);
    };
    groupEvents(id).forEach(e => {
      const a = eventTeam(e, 0),
        b = eventTeam(e, 1);
      ensure(a);
      ensure(b);
      const r = scoreFor(e);
      if (!r) return;
      const ar = ensure(a),
        br = ensure(b);
      ar.played++;
      br.played++;
      ar.gf += Number(r.home);
      ar.ga += Number(r.away);
      br.gf += Number(r.away);
      br.ga += Number(r.home);
      if (r.home > r.away) {
        ar.won++;
        br.lost++;
        ar.points += Number(tournament.scoring?.[0]?.score ?? 3);
        br.points += Number(tournament.scoring?.[3]?.score ?? 0);
      } else if (r.home < r.away) {
        br.won++;
        ar.lost++;
        br.points += Number(tournament.scoring?.[0]?.score ?? 3);
        ar.points += Number(tournament.scoring?.[3]?.score ?? 0);
      } else {
        ar.drawn++;
        br.drawn++;
        const pts = Number(
          tournament.scoring?.[Number(r.home) ? 1 : 2]?.score ?? 1
        );
        ar.points += pts;
        br.points += pts;
      }
      ar.gd = ar.gf - ar.ga;
      br.gd = br.gf - br.ga;
    });
    return [...map.values()].sort(
      (a, b) => b.points - a.points || b.gd - a.gd || b.gf - a.gf
    );
  };
  const matrixCell = (group: string, rowTeam: string, columnTeam: string) => {
    if (rowTeam === columnTeam) return null;
    const matchEvents = groupEvents(group).filter(item => {
      const a = eventTeam(item, 0).id,
        b = eventTeam(item, 1).id;
      return (
        (a === rowTeam && b === columnTeam) ||
        (a === columnTeam && b === rowTeam)
      );
    });
    if (!matchEvents.length) return undefined;
    const scores = matchEvents
      .map(event => {
        const result = scoreFor(event);
        if (!result) return undefined;
        const isHome = eventTeam(event, 0).id === rowTeam;
        return {
          own: isHome ? result.home : result.away,
          other: isHome ? result.away : result.home
        };
      })
      .filter(Boolean) as Array<{ own: number; other: number }>;
    if (!scores.length) return undefined;
    const own = scores.reduce((sum, item) => sum + Number(item.own), 0);
    const other = scores.reduce((sum, item) => sum + Number(item.other), 0);
    return {
      score: scores.map(item => `${item.own} : ${item.other}`).join(" · "),
      tone: own > other ? "is-win" : own < other ? "is-loss" : "is-draw"
    };
  };
  const displayedGroups = groupGroups.filter(group=>tab === "QUALIFIERS" ? qualifierIds.has(group.id) : !qualifierIds.has(group.id));
  const activeGroup = displayedGroups.find(group=>group.id===groupId)?.id || displayedGroups[0]?.id;
  const knockoutEvents = filtered.filter(event =>
    knockoutGroups.some(group => group.id === event.tournamentGroupId)
  );
  const hasPublishedKnockout = Boolean(
    structure &&
      (structure.matches || []).some(match =>
        structureGroups.some(group => group.id === match.groupId)
      )
  );
  const hasKnockoutContent = hasPublishedKnockout || knockoutEvents.length > 0;
  const mayRevealRuntimeKnockoutTeams = canRevealRuntimeKnockoutTeams(
    structure,
    runtime
  );
  const activeKnockoutGroup = structureGroups.find(
    group => group.id === (knockoutGroupId || structureGroups[0]?.id)
  );
  const activeKnockoutExplanation = activeKnockoutGroup
    ? structure?.groupRoutes?.find(
        route => route.groupId === activeKnockoutGroup.id
      )?.publicExplanation
    : "";
  const roundsForActiveGroup = activeKnockoutGroup
    ? qualifierIds.has(activeKnockoutGroup.id) ? [{id:"GROUP",label:"Round robin",shortLabel:"Round robin"}] : knockoutRounds.filter(
        round =>
          (structure?.matches || []).some(
            match =>
              match.groupId === activeKnockoutGroup.id &&
              matchRound(match, activeKnockoutGroup) === round.id
          ) ||
          knockoutEvents.some(
            event =>
              event.tournamentGroupId === activeKnockoutGroup.id &&
              matchRound(structureMatchForEvent(event), activeKnockoutGroup) ===
                round.id
          )
      )
    : [];
  const mobileKnockoutRoundIndex = Math.max(
    0,
    roundsForActiveGroup.findIndex(round => round.id === mobileKnockoutRoundId)
  );
  const mobileKnockoutRound = roundsForActiveGroup[mobileKnockoutRoundIndex];
  const visibleKnockoutRounds = isMobileKnockout
    ? mobileKnockoutRound ? [mobileKnockoutRound] : []
    : roundsForActiveGroup.filter(round => !hiddenKnockoutRounds.includes(round.id));
  const toggleKnockoutRound = (roundId: string) =>
    setHiddenKnockoutRounds(current =>
      current.includes(roundId)
        ? current.filter(id => id !== roundId)
        : current.length < roundsForActiveGroup.length - 1
        ? [...current, roundId]
        : current
    );
  const visibleFixtures = filtered.filter(
    event => !onlyMySchool || includesSchool(event, schoolId)
  );
  const fixtureDays = visibleFixtures.reduce<
    Array<{ key: string; label: string; events: SchoolEvent[] }>
  >((days, event) => {
    const scheduled = Number.isFinite(fixtureStart(event.startTime));
    const key = scheduled ? new Date(event.startTime).toLocaleDateString('en-CA') : 'unscheduled';
    let day = days.find(item => item.key === key);
    if (!day) {
      day = { key, label: scheduled ? date(event.startTime) : 'Time to be confirmed', events: [] };
      days.push(day);
    }
    day.events.push(event);
    return days;
  }, []);
  const renderKnockoutRound = (group: any, round: string) => {
    const actual = filtered.filter(
      event =>
        event.tournamentGroupId === group.id &&
        matchRound(structureMatchForEvent(event), group) === round
    );
    const planned = (structure?.matches || []).filter(match => {
      const liveMatch = runtime?.matches?.find(
        (item: any) => sid(item.matchId) === sid(match.id)
      );
      const eventId = liveMatch?.eventId || match.eventId;
      return (
        match.groupId === group.id &&
        matchRound(match, group) === round &&
        !events.some(event => sid(event) === sid(eventId))
      );
    });
    if (!actual.length && !planned.length) return null;
    const fixtures = [
      ...actual.map(event => ({ kind: "event" as const, event, id: structureMatchForEvent(event)?.id || event.id, startTime: event.startTime })),
      ...planned.map(match => ({ kind: "planned" as const, match, id: match.id, startTime: match.startTime }))
    ].sort(byFixtureStart);
    return (
      <section className="ttlBracket__branch" key={`${group.id}-${round}`}>
        <header>
          <h4>
            {round === "GROUP" ? "Round robin" : knockoutRounds.find(item => item.id === round)?.label || round}
          </h4>
        </header>
        <div className="ttlBracket__matches">
          {fixtures.map(fixture => {
            if (fixture.kind === "event") {
              return <MatchCard event={fixture.event} key={fixture.event.id} />;
            }
            const { match } = fixture;
            const liveMatch = runtime?.matches?.find(
              (item: any) => sid(item.matchId) === sid(match.id)
            );
            const home = tournamentTeamIdentity(
              (mayRevealRuntimeKnockoutTeams
                ? liveMatch?.homeTeamId
                : undefined) ||
                (match.homeSource.sourceType === "TEAM"
                  ? match.homeSource.teamId
                  : undefined)
            );
            const away = tournamentTeamIdentity(
              (mayRevealRuntimeKnockoutTeams
                ? liveMatch?.awayTeamId
                : undefined) ||
                (match.awaySource.sourceType === "TEAM"
                  ? match.awaySource.teamId
                  : undefined)
            );
            const isBye =
              match.homeSource.sourceType === "BYE" ||
              match.awaySource.sourceType === "BYE";
            return (
              <article
                className={`ttlPlanned ${isBye ? "is-bye" : ""}`}
                key={match.id || match.name}
              >
                <span>
                  {match.startTime
                    ? `${date(match.startTime)} · ${time(match.startTime)}`
                    : "Time TBC"}
                  {match.pitchId
                    ? ` · ${tournament.pitches?.find(
                        pitch => pitch.id === match.pitchId
                      )?.name || ""}`
                    : ""}
                </span>
                <h4>{match.name}</h4>
                {home ? (
                  <TeamLine team={home} origin={match.homeOriginLabel} />
                ) : (
                  <TeamLine team={{name: match.homeOriginLabel || sourceLabel(match.homeSource), logo: match.homeSource.sourceType === "BYE" ? undefined : school.pic}} />
                )}
                <i>{isBye ? "→" : "vs"}</i>
                {away ? (
                  <TeamLine team={away} origin={match.awayOriginLabel} />
                ) : (
                  <TeamLine team={{name: match.awayOriginLabel || sourceLabel(match.awaySource), logo: match.awaySource.sourceType === "BYE" ? undefined : school.pic}} />
                )}
                {(match.venue?.name || match.venue?.postcode) && (
                  <small>{match.venue.name || match.venue.postcode}</small>
                )}
                <small>
                  {isBye
                    ? round === "GROUP" ? "Bye — no fixture is required" : "The confirmed team advances automatically"
                    : home && away
                    ? "Fixture is being prepared"
                    : "Teams appear automatically when decided"}
                </small>
              </article>
            );
          })}
        </div>
      </section>
    );
  };
  const unmaterializedKnockoutMatches = (runtime?.matches || []).filter(
    (match: any) => match.status !== "BYE" && !match.eventId
  ).length;
  const matchTotal = events.length + unmaterializedKnockoutMatches,
    doneTotal = events.filter(completed).length,
    progress = matchTotal ? Math.round((doneTotal / matchTotal) * 100) : 0;
  const delayMinutes = liveAll.reduce(
    (delay, event) =>
      Math.max(
        delay,
        Math.floor(
          (now - new Date(event.endTime || event.startTime).getTime()) / 60000
        )
      ),
    0
  );
  const groupStageComplete = Boolean(runtime?.groupReview?.allGroupsComplete);
  const knockoutAwaitingConfirmation =
    groupStageComplete &&
    ["READY", "REQUIRES_DECISION", "STALE"].includes(
      runtime?.groupReview?.status
    ) &&
    unmaterializedKnockoutMatches > 0;
  const betweenGroupAndKnockout =
    groupStageComplete &&
    unmaterializedKnockoutMatches > 0 &&
    !liveAll.length &&
    !upcomingAll.length;
  const scheduleStatus = knockoutAwaitingConfirmation
    ? "Group stage complete · knockout fixtures are being confirmed"
    : betweenGroupAndKnockout
    ? "Group stage complete · knockout matches coming soon"
    : doneTotal === matchTotal && matchTotal
    ? "Tournament complete"
    : delayMinutes > 5
    ? `Running around ${delayMinutes} min behind schedule`
    : liveAll.length
    ? "Running on time"
    : upcomingAll.length
    ? "Schedule on time"
    : "Ready for the first match";
  return (
    <div className={`ttlSite ${embedded ? "is-embedded" : ""}`}>
      {!embedded && (
        <header className="ttlHeader">
          <div className="ttlHeader__main">
            <span className="ttlHeader__logo">
              {school.pic ? (
                <img src={school.pic} alt="" />
              ) : (
                initials(school.name || "SIT")
              )}
            </span>
            <div>
              <h1>{tournament.name}</h1>
              <p>
                {tournamentDates}
                {(tournament.venue as any)?.placeData?.name
                  ? ` · ${(tournament.venue as any).placeData.name}`
                  : ""}
              </p>
            </div>
            <FollowerRegistration school={school} />
            <label>
              <span>⌕</span>
              <input
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Find a school or team"
              />
            </label>
            <button
              className={`ttlLiveStatus ${liveAll.length ? "is-live" : ""}`}
              type="button"
              onClick={() => refresh(false)}
              title="Refresh live tournament data"
            >
              <i aria-hidden="true" />
              <span>
                {liveAll.length
                  ? `Live · ${liveAll.length} ${
                      liveAll.length === 1 ? "match" : "matches"
                    } on`
                  : "Live updates"}
              </span>
            </button>
          </div>
          <nav>
            {tabs.filter(item=>item.id!=="QUALIFIERS"||qualifierIds.size>0).map(item => (
              <button
                className={tab === item.id ? "is-active" : ""}
                onClick={() => setTab(item.id)}
                key={item.id}
              >
                {item.label}
              </button>
            ))}
          </nav>
        </header>
      )}
      {embedded && (
        <header className="ttlHeader ttlHeader--embedded">
          <div className="ttlHeader__main">
            <div>
              <h1>{tournament.name}</h1>
              <p>
                {tournamentDates}
                {(tournament.venue as any)?.placeData?.name
                  ? ` · ${(tournament.venue as any).placeData.name}`
                  : ""}
              </p>
            </div>
            <label>
              <span>⌕</span>
              <input
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Find a school or team"
              />
            </label>
            <button
              className={`ttlLiveStatus ${liveAll.length ? "is-live" : ""}`}
              type="button"
              onClick={() => refresh(false)}
              title="Refresh live tournament data"
            >
              <i aria-hidden="true" />
              <span>
                {liveAll.length
                  ? `Live · ${liveAll.length} ${
                      liveAll.length === 1 ? "match" : "matches"
                    } on`
                  : "Live updates"}
              </span>
            </button>
          </div>
          <nav>
            {tabs.filter(item=>item.id!=="QUALIFIERS"||qualifierIds.size>0).map(item => (
              <button
                className={tab === item.id ? "is-active" : ""}
                onClick={() => setTab(item.id)}
                key={item.id}
              >
                {item.label}
              </button>
            ))}
          </nav>
        </header>
      )}
      {!embedded && tournament.photos?.[0]?.picUrl && tab === "NOW" && (
        <div
          className="ttlBanner"
          style={{
            backgroundImage: `linear-gradient(90deg,rgba(8,29,51,.82),rgba(8,29,51,.18)),url(${tournament.photos[0].picUrl})`
          }}
        >
          <span>Live tournament</span>
          <h2>{tournament.name}</h2>
          <p>
            {tournament.description ||
              `${tournament.sport?.name ||
                "Sport"} tournament fixtures, results and standings.`}
          </p>
        </div>
      )}
      <main className="ttlMain">
        {error && <div className="ttlNotice">{error}</div>}
        {isLoading && <div className="ttlEmpty">Loading live tournament…</div>}
        {!isLoading && tab === "NOW" && (
          <>
            <section className="ttlProgress">
              <div
                className={`ttlProgress__status ${
                  delayMinutes > 5 ? "is-delayed" : ""
                }`}
              >
                <i />
                <strong>{scheduleStatus}</strong>
              </div>
              <div>
                <h2>
                  {knockoutAwaitingConfirmation || betweenGroupAndKnockout
                    ? "Group stage complete"
                    : doneTotal === matchTotal && matchTotal
                    ? "Tournament complete"
                    : isMultiDay
                    ? "Tournament progress"
                    : "Tournament day"}
                </h2>
                <span>{progress}% complete</span>
              </div>
              <i>
                <b style={{ width: `${progress}%` }} />
              </i>
              <dl>
                <div>
                  <dt>{matchTotal}</dt>
                  <dd>Matches</dd>
                </div>
                <div>
                  <dt>{doneTotal}</dt>
                  <dd>Finished</dd>
                </div>
                <div>
                  <dt>{liveAll.length}</dt>
                  <dd>Live now</dd>
                </div>
                <div>
                  <dt>
                    {Math.max(0, matchTotal - doneTotal - liveAll.length)}
                  </dt>
                  <dd>Still to play</dd>
                </div>
              </dl>
            </section>
            <div className="ttlDashboard">
              <div>
                <section className="ttlSection">
                  <h2>On the pitches now</h2>
                  <div className="ttlCardGrid">
                    {live.map(e => (
                      <MatchCard event={e} key={e.id} />
                    ))}
                    {!live.length && (
                      <div className="ttlEmptyCard">
                        <b>No matches are live right now</b>
                        <span>
                          {upcomingAll[0]
                            ? `Next match: ${shortDate(
                                upcomingAll[0].startTime
                              )} at ${time(upcomingAll[0].startTime)}.`
                            : knockoutAwaitingConfirmation
                            ? "The knockout line-up is being confirmed."
                            : betweenGroupAndKnockout
                            ? "Knockout fixtures will appear here shortly."
                            : "Check back when play begins."}
                        </span>
                      </div>
                    )}
                  </div>
                </section>
                <section className="ttlSection">
                  <header>
                    <h2>Next matches</h2>
                    <button onClick={() => setTab("FIXTURES")}>
                      All fixtures
                    </button>
                  </header>
                  <div className="ttlRows">
                    {upcoming.slice(0, 6).map(e => (
                      <MatchCard compact event={e} key={e.id} />
                    ))}
                    {!upcoming.length && (
                      <div className="ttlEmptyCard">
                        {knockoutAwaitingConfirmation
                          ? "Knockout fixtures are awaiting organiser confirmation."
                          : betweenGroupAndKnockout
                          ? "Knockout matches are coming soon."
                          : "No more scheduled fixtures."}
                      </div>
                    )}
                  </div>
                </section>
                {finished.length > 0 && (
                  <section className="ttlSection">
                    <header>
                      <h2>Just finished</h2>
                      <button onClick={() => setTab("FIXTURES")}>
                        All results
                      </button>
                    </header>
                    <div className="ttlCardGrid">
                      {finished.slice(-4).map(e => (
                        <MatchCard event={e} key={e.id} />
                      ))}
                    </div>
                  </section>
                )}
              </div>
              <aside>
                <section className="ttlAsideCard">
                  <header>
                    <h3>Where the groups stand</h3>
                    <button onClick={() => setTab("GROUPS")}>All groups</button>
                  </header>
                  {groupGroups.length > 1 && (
                    <div className="ttlStandingsFilters">
                      {standingsStages.length > 1 && <label>
                        Stage
                        <select value={selectedStandingsStage} onChange={event => {
                          setStandingsStageId(event.target.value);
                          setStandingsGroupId('');
                        }}>
                          <option value="">All stages</option>
                          {standingsStages.map(stage => <option key={stage.id} value={stage.id}>{stage.name}</option>)}
                        </select>
                      </label>}
                      <label>
                        Group
                        <select value={selectedStandingsGroup} onChange={event => setStandingsGroupId(event.target.value)}>
                          <option value="">All groups</option>
                          {standingsGroupOptions.map(group => <option key={group.id} value={group.id}>{group.name}</option>)}
                        </select>
                      </label>
                    </div>
                  )}
                  {dashboardStandings.map(g => (
                    <div className="ttlMiniTable" key={g.id}>
                      <b>{g.name}</b>
                      {standings(g.id).map((r, i) => (
                        <span className="ttlMiniTable__row" key={r.team.id}>
                          <i>{i + 1}</i>
                          <TeamLine team={r.team} />
                          <strong>{r.points}</strong>
                        </span>
                      ))}
                    </div>
                  ))}
                </section>
                <section className="ttlFollow">
                  <h3>
                    {followedSchool
                      ? `Following ${followedSchool.name}`
                      : "Following one school?"}
                  </h3>
                  <p>
                    {followedSchool
                      ? "Your school is saved on this device. Open all of its tournament fixtures."
                      : "Choose a school once and keep its fixtures and results together."}
                  </p>
                  <button onClick={() => setTab("SCHOOLS")}>
                    {followedSchool ? "Open my school" : "Choose a school"}
                  </button>
                  {followedSchool && (
                    <button
                      className="is-secondary"
                      onClick={() => chooseSchool("")}
                    >
                      Stop following
                    </button>
                  )}
                </section>
              </aside>
            </div>
            {tournament.sponsors?.length > 0 && (
              <section className="ttlSponsors">
                <header>
                  <span>With thanks to</span>
                  <h2>Tournament sponsors</h2>
                </header>
                <div>
                  {tournament.sponsors.map((sponsor, index) => {
                    const href = sponsorHref(sponsor.link);
                    const body = (
                      <>
                        {sponsor.picUrl ? (
                          <img src={sponsor.picUrl} alt="" />
                        ) : (
                          <i>{initials(sponsor.name || "Sponsor")}</i>
                        )}
                        <strong>{sponsor.name || "Tournament sponsor"}</strong>
                        {href && <SponsorQR className="ttlSponsors__qr" value={href} />}
                      </>
                    );
                    return href ? (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        key={`${sponsor.name}_${index}`}
                      >
                        {body}
                      </a>
                    ) : (
                      <article key={`${sponsor.name}_${index}`}>{body}</article>
                    );
                  })}
                </div>
              </section>
            )}
          </>
        )}
        {!isLoading &&
          (tab === "GROUPS" || tab === "QUALIFIERS") &&
          (displayedGroups.length ? (
            <>
              <div className="ttlGroupTools">
                <div className="ttlChips">
                  {displayedGroups.map(g => (
                    <button
                      className={activeGroup === g.id ? "is-active" : ""}
                      onClick={() => setGroupId(g.id)}
                      key={g.id}
                    >
                      {g.name}
                    </button>
                  ))}
                </div>
                {events.some(event=>event.tournamentGroupId===activeGroup)&&<button
                  className={`ttlMatrixToggle ${showMatrix ? "is-active" : ""}`}
                  onClick={() => setShowMatrix(value => !value)}
                >
                  <i />
                  <span>Head-to-head matrix</span>
                </button>}
              </div>
              {displayedGroups
                .filter(g => g.id === activeGroup)
                .map(g => {
                  const rows = standings(g.id);
                  const hasGroupEvents = events.some(event=>event.tournamentGroupId===g.id);
                  const route = structure?.groupRoutes?.find(
                    x => x.groupId === g.id
                  );
                  const destinations = (
                    route?.destinations || []
                  ).filter(item => item.title?.trim());
                  const hasRouteDescription = Boolean(destinations.length);
                  const hasGroupRules = Boolean(
                    structure?.groupRulesPublicSummary?.trim() ||
                      structure?.tieBreakers?.length
                  );
                  const overall =
                    g.competitionOutcome === "OVERALL_STANDINGS";
                  const groupComplete =
                    Boolean(groupEvents(g.id).length) &&
                    groupEvents(g.id).every(event => Boolean(scoreFor(event)));
                  const awardPlaces = Math.max(1, Number(g.awardPlaces) || 3);
                  return (
                    <section className="ttlGroupPage" key={g.id}>
                      <header>
                        <h2>{g.name}</h2>
                        <p>
                          {qualifierIds.has(g.id) ? Math.max(groupEvents(g.id).length,(structure?.matches||[]).filter(match=>match.groupId===g.id&&match.homeSource.sourceType!=="BYE"&&match.awaySource.sourceType!=="BYE").length) : groupEvents(g.id).length} fixtures
                          {Number(g.roundRobinRounds) > 1
                            ? ` · ${g.roundRobinRounds} rounds`
                            : ""}
                          {overall
                            ? " · final positions come from the overall table"
                            : " · standings update with published results"}
                        </p>
                      </header>
                      {overall && groupComplete && (
                        <section className="ttlOverallPodium">
                          <header>
                            <span>Competition complete</span>
                            <h3>Final positions</h3>
                          </header>
                          <div>
                            {rows.slice(0, awardPlaces).map((row, index) => (
                              <article
                                className={`is-place-${index + 1}`}
                                key={row.team.id}
                              >
                                <i>{index + 1}</i>
                                <TeamLine team={row.team} />
                                <strong>{row.points} pts</strong>
                              </article>
                            ))}
                          </div>
                        </section>
                      )}
                      {(hasGroupEvents||hasRouteDescription||hasGroupRules)&&<div
                        className={`ttlGroupLayout ${
                          !hasGroupEvents || !hasRouteDescription && !hasGroupRules
                            ? "is-full-width"
                            : ""
                        }`}
                      >
                        {hasGroupEvents&&<div>
                          {rows.length>0&&<div className="ttlStandings">
                            <div className="ttlStandings__head">
                              <span>#</span>
                              <span>School / team</span>
                              <span>P</span>
                              <span>W</span>
                              <span>D</span>
                              <span>L</span>
                              <span>GF</span>
                              <span>GA</span>
                              <span>GD</span>
                              <span>Pts</span>
                            </div>
                            {rows.map((r, i) => (
                              <div
                                className={
                                  !r.isResolved && r.played
                                    ? "is-unresolved"
                                    : ""
                                }
                                key={r.team.id}
                              >
                                <span>{i + 1}</span>
                                <TeamLine team={r.team} />
                                <span>{r.played}</span>
                                <span>{r.won}</span>
                                <span>{r.drawn}</span>
                                <span>{r.lost}</span>
                                <span>{r.gf}</span>
                                <span>{r.ga}</span>
                                <span>{r.gd}</span>
                                <strong>{r.points}</strong>
                              </div>
                            ))}
                          </div>}
                          {showMatrix && rows.length>0 && (
                            <section className="ttlMatrix">
                              <header>
                                <h3>Head-to-head matrix</h3>
                                <p>
                                  Each cell shows the score from the team on
                                  that row. Multiple rounds are shown in
                                  playing order.
                                </p>
                              </header>
                              <div className="ttlMatrix__scroll">
                                <div
                                  className="ttlMatrix__grid"
                                  style={{
                                    gridTemplateColumns: `minmax(170px,1fr) repeat(${rows.length},minmax(92px,1fr))`
                                  }}
                                >
                                  <span />
                                  <>
                                    {rows.map(row => (
                                      <b key={row.team.id}>
                                        {row.schoolName || row.name}
                                      </b>
                                    ))}
                                  </>
                                  {rows.flatMap(row => (
                                    <React.Fragment key={row.team.id}>
                                      <strong>
                                        {row.schoolName || row.name}
                                      </strong>
                                      {rows.map(column => {
                                        const cell = matrixCell(
                                          g.id,
                                          row.team.id,
                                          column.team.id
                                        );
                                        return (
                                          <span
                                            className={
                                              cell === null
                                                ? "is-self"
                                                : cell?.tone || ""
                                            }
                                            key={column.team.id}
                                          >
                                            {cell === null
                                              ? "—"
                                              : cell?.score || "To play"}
                                          </span>
                                        );
                                      })}
                                    </React.Fragment>
                                  ))}
                                </div>
                              </div>
                            </section>
                          )}
                          {groupEvents(g.id).length>0&&<><h3 className="ttlMatchesTitle">Group matches</h3>
                          <div className="ttlRows">
                            {groupEvents(g.id).map(e => (
                              <MatchCard compact event={e} key={e.id} />
                            ))}
                          </div></>}
                        </div>}
                        {(hasRouteDescription || hasGroupRules) && (
                          <aside className="ttlRoute">
                            {hasRouteDescription && (
                              <>
                                <h3>Where this group goes</h3>
                                {destinations.map((destination, index) => (
                                  <div
                                    className="ttlRoute__destination"
                                    key={`${destination.title}-${index}`}
                                  >
                                    <h4>
                                      {destination.title}
                                      {destination.places && (
                                        <small>{destination.places}</small>
                                      )}
                                    </h4>
                                    {destination.publicExplanation && (
                                      <p>{destination.publicExplanation}</p>
                                    )}
                                  </div>
                                ))}
                              </>
                            )}
                            {hasGroupRules && (
                              <>
                                {structure?.groupRulesPublicSummary && (
                                  <>
                                    <h3>Group rules</h3>
                                    <p>{structure.groupRulesPublicSummary}</p>
                                  </>
                                )}
                                {Boolean(structure?.tieBreakers?.length) && (
                                  <>
                                    <h3>How level teams are separated</h3>
                                    <ol className="ttlTieBreakers">
                                      {structure!.tieBreakers.map(
                                        (item, index) => (
                                          <li
                                            key={`${item.criterion}-${index}`}
                                          >
                                            <b>{index + 1}</b>
                                            <span>
                                              {tieBreakerLabels[
                                                item.criterion
                                              ] ||
                                                item.criterion
                                                  .toLowerCase()
                                                  .replace(/_/g, " ")}
                                            </span>
                                          </li>
                                        )
                                      )}
                                    </ol>
                                  </>
                                )}
                              </>
                            )}
                          </aside>
                        )}
                      </div>}
                      {qualifierIds.has(g.id)&&<div className="ttlQualifierFixtures">{renderKnockoutRound(g,"GROUP")}</div>}
                    </section>
                  );
                })}
            </>
          ) : (
            <section className="ttlPageEmpty">
              <span aria-hidden="true">◎</span>
              <h2>Groups coming soon</h2>
              <p>The organiser has not published the group stage yet.</p>
            </section>
          ))}
        {!isLoading && tab === "KNOCKOUT" && (
          <section className="ttlKnockout">
            <header>
              <h2>Knockout Stages</h2>
              <p>
                {hasKnockoutContent
                  ? "Follow every route through to the finals. The bracket updates as teams are decided."
                  : "The knockout route will appear here when it is ready."}
              </p>
            </header>
            {hasKnockoutContent && activeKnockoutGroup ? (
              <>
                <nav className="ttlKnockout__competitions">
                  {structureGroups.map(group => (
                    <button
                      type="button"
                      className={
                        activeKnockoutGroup.id === group.id ? "is-active" : ""
                      }
                      onClick={() => {
                        setKnockoutGroupId(group.id);
                        setHiddenKnockoutRounds([]);
                        setMobileKnockoutRoundId("");
                      }}
                      key={group.id}
                    >
                      {group.name}
                    </button>
                  ))}
                </nav>
                {qualifierIds.has(activeKnockoutGroup.id)&&<div className="ttlKnockout__roundFilter"><span>Qualifier group · round robin</span><button type="button" className="ttlKnockout__standingsLink" onClick={()=>{setGroupId(activeKnockoutGroup.id);setTab("QUALIFIERS");}}>View group standings</button></div>}
                {roundsForActiveGroup.length > 1 && isMobileKnockout && (
                  <div className="ttlKnockout__stagePicker">
                    <button
                      type="button"
                      aria-label="Previous knockout stage"
                      disabled={mobileKnockoutRoundIndex === 0}
                      onClick={() => setMobileKnockoutRoundId(roundsForActiveGroup[mobileKnockoutRoundIndex - 1].id)}
                    >
                      ‹
                    </button>
                    <label>
                      <span>Stage</span>
                      <select
                        aria-label="Knockout stage"
                        value={mobileKnockoutRound?.id || ""}
                        onChange={event => setMobileKnockoutRoundId(event.target.value)}
                      >
                        {roundsForActiveGroup.map(round => (
                          <option value={round.id} key={round.id}>{round.label}</option>
                        ))}
                      </select>
                    </label>
                    <button
                      type="button"
                      aria-label="Next knockout stage"
                      disabled={mobileKnockoutRoundIndex === roundsForActiveGroup.length - 1}
                      onClick={() => setMobileKnockoutRoundId(roundsForActiveGroup[mobileKnockoutRoundIndex + 1].id)}
                    >
                      ›
                    </button>
                  </div>
                )}
                {roundsForActiveGroup.length > 1 && !isMobileKnockout && (
                  <div className="ttlKnockout__roundFilter">
                    <span>Show rounds</span>
                    <nav className="ttlKnockout__rounds">
                      {roundsForActiveGroup.map(round => (
                        <button
                          type="button"
                          aria-pressed={
                            !hiddenKnockoutRounds.includes(round.id)
                          }
                          className={
                            !hiddenKnockoutRounds.includes(round.id)
                              ? "is-active"
                              : ""
                          }
                          onClick={() => toggleKnockoutRound(round.id)}
                          key={round.id}
                        >
                          <i />
                          {round.label}
                        </button>
                      ))}
                    </nav>
                    <button
                      type="button"
                      className="ttlKnockout__showAll"
                      disabled={!hiddenKnockoutRounds.length}
                      onClick={() => setHiddenKnockoutRounds([])}
                    >
                      Show all
                    </button>
                  </div>
                )}
                {activeKnockoutExplanation && (
                  <aside className="ttlKnockout__competitionNote">
                    <strong>{activeKnockoutGroup.name}</strong>
                    <p>{activeKnockoutExplanation}</p>
                  </aside>
                )}
                <div
                  className={`ttlBracket ttlBracket--flow ${
                    visibleKnockoutRounds.length === 1 ? "is-single-round" : ""
                  }`}
                >
                  {visibleKnockoutRounds.map(round =>
                    renderKnockoutRound(activeKnockoutGroup, round.id)
                  )}
                </div>
              </>
            ) : hasKnockoutContent ? (
              <div className="ttlEmpty">
                The published knockout structure has no competitions yet.
              </div>
            ) : (
              <div className="ttlKnockoutEmpty" role="status">
                <span className="ttlKnockoutEmpty__mark" aria-hidden="true">
                  ◇
                </span>
                <h3>Knockout structure coming soon</h3>
                <p>
                  The organiser is still preparing the route to the finals.
                  Fixtures and teams will appear here once the structure is
                  published.
                </p>
                <button type="button" onClick={() => setTab("FIXTURES")}>
                  View fixtures &amp; results
                </button>
              </div>
            )}
          </section>
        )}
        {!isLoading && tab === "FIXTURES" && (
          <section className="ttlFixtures">
            <header>
              <h2>Fixtures & results</h2>
            </header>
            <div className="ttlFixtureFilters">
              <button
                className={!onlyMySchool ? "is-active" : ""}
                onClick={() => setOnlyMySchool(false)}
              >
                Everything
              </button>
              {followedSchool && (
                <button
                  className={onlyMySchool ? "is-active" : ""}
                  onClick={() => setOnlyMySchool(true)}
                >
                  My school · {followedSchool.name}
                </button>
              )}
            </div>
            {fixtureDays.length ? (
              <div className="ttlFixtureDays">
                {fixtureDays.map(day => (
                  <section key={day.key}>
                    <header>
                      <div>
                        <span>Tournament day</span>
                        <h3>{day.label}</h3>
                      </div>
                      <b>
                        {day.events.length}{" "}
                        {day.events.length === 1 ? "match" : "matches"}
                      </b>
                    </header>
                    <div className="ttlRows">
                      {day.events.map(event => (
                        <MatchCard compact event={event} key={event.id} />
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            ) : (
              <div className="ttlEmpty">No matching fixtures.</div>
            )}
          </section>
        )}
        {!isLoading && tab === "SCHOOLS" && (
          <section className="ttlSchoolsPage">
            <header>
              <h2>{followedSchool ? followedSchool.name : "Schools"}</h2>
              <p>
                {followedSchool
                  ? "Your saved school · every team, fixture and result in one place."
                  : "Choose a school to follow all of its tournament fixtures."}
              </p>
            </header>
            {!followedSchool && (
              <div className="ttlSchoolChips">
                {schoolList.map(item => (
                  <button onClick={() => chooseSchool(item.id)} key={item.id}>
                    {item.pic ? (
                      <img src={item.pic} alt="" />
                    ) : (
                      <span>{initials(item.name)}</span>
                    )}
                    <b>{item.name}</b>
                    <small>Follow</small>
                  </button>
                ))}
              </div>
            )}
            {followedSchool && (
              <div className="ttlSchoolDay">
                <div className="ttlSchoolDay__identity">
                  {followedSchool.pic ? (
                    <img src={followedSchool.pic} alt="" />
                  ) : (
                    <span>{initials(followedSchool.name)}</span>
                  )}
                  <div>
                    <h3>{followedSchool.name}</h3>
                    <p>
                      {
                        filtered.filter(event =>
                          includesSchool(event, schoolId)
                        ).length
                      }{" "}
                      matches in this tournament
                    </p>
                  </div>
                  <button onClick={() => chooseSchool("")}>
                    Choose another school
                  </button>
                </div>
                <div className="ttlRows">
                  {filtered
                    .filter(event => includesSchool(event, schoolId))
                    .map(e => (
                      <MatchCard compact event={e} key={e.id} />
                    ))}
                </div>
              </div>
            )}
          </section>
        )}
      </main>
      {!embedded && (
        <footer className="ttlFooter">
          <span>Powered by Squad In Touch</span>
          <span>{tournament.organisersEmail}</span>
        </footer>
      )}
    </div>
  );
}
